import moment from 'moment-timezone';
import { getOrgTimeZone } from './tokenManager';

/**
 * Mirror of the backend's exam start-time rule (CVA-4154, rule R1).
 *
 * Per subject, evaluated in the ORGANIZATION timezone:
 *  - a start date BEFORE today is rejected;
 *  - a start date OF TODAY must be at least `EXAM_MIN_START_LEAD_MINUTES`
 *    ahead of now — the buffer exists because the teacher goes on to build a
 *    paper after choosing the time, and the exam must still be in the future
 *    once that work finishes;
 *  - a FUTURE date carries no buffer requirement.
 *
 * The backend remains the enforcing layer; this exists so the common case is
 * caught instantly, in the form, instead of after a paper has been built.
 *
 * Times are compared in the org timezone because that is how the backend reads
 * them: the payload carries a wall-clock string, which the API interprets
 * against `user.orgTimeZone`. Validating against the BROWSER's clock would
 * disagree with the server for anyone working outside the school's timezone.
 */

/** Keep in step with `EXAM_MIN_START_LEAD_MINUTES` on the backend. */
export const EXAM_MIN_START_LEAD_MINUTES = 10;

export interface IExamStartTimeCandidate {
  /** Display name used in the failure message. */
  subjectLabel: string;
  /** Exam date as held by the form (a Date, or a parseable date string). */
  date: Date | string | null | undefined;
  /** Wall-clock start time, `HH:mm`, as held by the form. */
  startTime: string | null | undefined;
  /** Subject status; a subject being cancelled is skipped, as on the backend. */
  status?: string;
  /**
   * The stored start time for an existing subject, when editing. Supplied only
   * on the edit flow: a subject whose time is unchanged is skipped entirely
   * (backend rule 1.6), otherwise a past-dated exam could not be edited at all
   * — including not rescheduled, which is the only way to rescue one.
   */
  storedStartTime?: Date | string | null;
}

export interface IExamStartTimeViolations {
  /** Subject names whose start date is before today. */
  pastSubjects: string[];
  /** Subject names dated today but inside the lead-time buffer. */
  tooSoonSubjects: string[];
}

const CANCELLED_STATUS = 'CANCELLED';

/** Resolve the org timezone, falling back to the browser's when unavailable. */
const resolveOrgTimeZone = (): string => getOrgTimeZone() || moment.tz.guess() || 'UTC';

/**
 * Combine the form's separate date and `HH:mm` fields into a single moment in
 * the organization timezone. Returns null when either part is missing, so an
 * incomplete row is simply not judged.
 */
const toOrgMoment = (
  date: Date | string | null | undefined,
  startTime: string | null | undefined,
  timeZone: string,
): moment.Moment | null => {
  if (!date || !startTime) return null;

  const day = moment(date);
  if (!day.isValid()) return null;

  const [hours, minutes] = startTime.split(':');
  if (hours === undefined || minutes === undefined) return null;

  // Build the wall-clock string the payload will carry, then read it in the org
  // timezone — exactly the round trip the backend performs.
  const wallClock = `${day.format('YYYY-MM-DD')}T${hours.padStart(2, '0')}:${minutes.padStart(
    2,
    '0',
  )}:00`;
  const instant = moment.tz(wallClock, timeZone);
  return instant.isValid() ? instant : null;
};

/**
 * Which subjects break rule R1. Returns empty lists when everything passes, so
 * callers can treat a falsy total as "go ahead".
 */
export const findExamStartTimeViolations = (
  candidates: IExamStartTimeCandidate[],
): IExamStartTimeViolations => {
  const timeZone = resolveOrgTimeZone();
  const now = moment.tz(timeZone);
  const startOfToday = now.clone().startOf('day');
  const earliestAllowedToday = now.clone().add(EXAM_MIN_START_LEAD_MINUTES, 'minutes');

  const pastSubjects: string[] = [];
  const tooSoonSubjects: string[] = [];

  candidates.forEach((candidate) => {
    // A subject being cancelled is on its way out — the backend skips it too.
    if (candidate.status === CANCELLED_STATUS) return;

    const start = toOrgMoment(candidate.date, candidate.startTime, timeZone);
    if (!start) return;

    // Rule 1.6 — on edit, only a time the user actually changed is judged.
    if (candidate.storedStartTime) {
      const stored = moment.tz(moment(candidate.storedStartTime).toISOString(), timeZone);
      if (stored.isValid() && stored.isSame(start)) return;
    }

    if (start.isBefore(startOfToday)) {
      pastSubjects.push(candidate.subjectLabel);
      return;
    }

    if (start.isSame(now, 'day') && start.isBefore(earliestAllowedToday)) {
      tooSoonSubjects.push(candidate.subjectLabel);
    }
  });

  return { pastSubjects, tooSoonSubjects };
};

/**
 * User-facing message for the violations, or null when there are none. Lists
 * every offending subject by name so a multi-subject clash is one message
 * rather than one attempt per subject — matching the backend's wording.
 *
 * `omitSubjects` selects the field-level phrasing ("Start time cannot be in the
 * past.") used when the message is rendered directly on the start-time input,
 * where naming the subject is redundant. It exists as an explicit option
 * because the caller used to post-process the English text with
 * `.replace('Start time for this subject ', 'Start time ')` — a substring match
 * that silently stops matching once the message is translated, leaking the
 * subject prefix into every non-English locale.
 */
export const buildExamStartTimeErrorMessage = (
  violations: IExamStartTimeViolations,
  t: (key: string, def?: string, vars?: Record<string, string | number>) => string,
  options?: { omitSubjects?: boolean },
): string | null => {
  const parts: string[] = [];
  const omit = options?.omitSubjects === true;

  if (violations.pastSubjects.length > 0) {
    parts.push(
      omit
        ? t('exam_event_form.error_start_time_past_field', 'Start time cannot be in the past.')
        : t(
            'exam_event_form.error_start_time_past',
            'Start time for {{subjects}} cannot be in the past.',
            { subjects: violations.pastSubjects.join(', ') },
          ),
    );
  }
  if (violations.tooSoonSubjects.length > 0) {
    parts.push(
      omit
        ? t(
            'exam_event_form.error_start_time_too_soon_field',
            'Start time must be at least {{minutes}} minutes from now.',
            { minutes: EXAM_MIN_START_LEAD_MINUTES },
          )
        : t(
            'exam_event_form.error_start_time_too_soon',
            'Start time for {{subjects}} must be at least {{minutes}} minutes from now.',
            {
              subjects: violations.tooSoonSubjects.join(', '),
              minutes: EXAM_MIN_START_LEAD_MINUTES,
            },
          ),
    );
  }

  return parts.length > 0 ? parts.join(' ') : null;
};

/**
 * Whether a subject's scheduled start has already been reached, in the ORG
 * timezone — the client-side mirror of the backend's AC4 paper lock
 * (`ExamPaperUtils.getPaperAuthoringLock`).
 *
 * Kept in this file rather than `exam.constant.ts` because the org-timezone
 * conversion lives here: judging "has it started" against the BROWSER clock
 * would disagree with the server for anyone working outside the school's
 * timezone, exactly as it would for the start-time rule above.
 *
 * The backend remains the enforcing layer. This exists so a started subject is
 * shown as locked instead of inviting the teacher to author a paper the save
 * will refuse.
 */
export const hasSubjectStarted = (
  date: Date | string | null | undefined,
  startTime: string | null | undefined,
): boolean => {
  const timeZone = resolveOrgTimeZone();
  const start = toOrgMoment(date, startTime, timeZone);
  if (!start) return false;
  return !moment.tz(timeZone).isBefore(start);
};

/**
 * Whether a subject's scheduled END has already been reached, in the ORG
 * timezone — sibling to `hasSubjectStarted` above, used to tell "in progress"
 * apart from "finished" for display purposes (CVA-4734). There is no
 * server-side transition off `PENDING`/`DRAFT` when a sitting ends either
 * (only a teacher's manual "start marking" action moves a subject on), so a
 * subject can be judged finished purely by elapsed time, the same way
 * `hasSubjectStarted` judges "in progress".
 */
export const hasSubjectFinished = (
  date: Date | string | null | undefined,
  endTime: string | null | undefined,
): boolean => {
  const timeZone = resolveOrgTimeZone();
  const end = toOrgMoment(date, endTime, timeZone);
  if (!end) return false;
  return !moment.tz(timeZone).isBefore(end);
};

/**
 * Whether an exam date falls on today in the ORGANIZATION timezone — the same
 * frame the backend uses to decide whether a save publishes the exam (rule R2).
 */
export const isExamDateToday = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;

  const timeZone = resolveOrgTimeZone();
  const day = moment(date);
  if (!day.isValid()) return false;

  return day.format('YYYY-MM-DD') === moment.tz(timeZone).format('YYYY-MM-DD');
};

/** Default shape of `convertUtcToLocal`'s output on the backend — no zone marker. */
const ORG_LOCAL_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Whether "now" is still before an already org-local-FORMATTED datetime
 * string (e.g. `IExamPaperView.startTime`, CVA-4197 plan 0009) — without
 * re-running timezone conversion on that already-local value, which is
 * exactly the bug plan 0006 hit and fixed once already (see
 * `IExamPaperView.startTime`'s doc comment).
 *
 * Both sides are reduced to a naive, zone-less wall-clock string in the org
 * timezone before comparing: `localDateTime` is parsed as-is (it already IS
 * the org's wall clock), and "now" is converted to the org's wall clock the
 * same way. Comparing two naive strings this way stays correct regardless of
 * the browser's own timezone, without ever converting `localDateTime` itself.
 */
export const isBeforeOrgNow = (
  localDateTime: string,
  format: string = ORG_LOCAL_DATETIME_FORMAT,
): boolean => {
  const timeZone = resolveOrgTimeZone();
  const orgNow = moment.tz(timeZone).format(format);
  return moment(orgNow, format).isBefore(moment(localDateTime, format));
};

/**
 * The earliest selectable `HH:mm` for a given exam date — used to stop a past
 * time being picked for today at all, rather than only reporting it afterwards.
 * Returns undefined for any other date, which leaves the field unrestricted.
 */
export const minSelectableStartTime = (
  date: Date | string | null | undefined,
): string | undefined => {
  if (!date) return undefined;

  const timeZone = resolveOrgTimeZone();
  const now = moment.tz(timeZone);
  const day = moment(date);
  if (!day.isValid()) return undefined;

  const isToday = day.format('YYYY-MM-DD') === now.format('YYYY-MM-DD');
  return isToday ? now.format('HH:mm') : undefined;
};
